import React, { useState, useEffect } from 'react';
import domReady from '@wordpress/dom-ready';
import { createRoot } from '@wordpress/element';
import {  Panel, PanelBody, PanelRow, Button, Spinner } from '@wordpress/components';
import Cookies from 'js-cookie';

import Table from './admin/EditableTable';
import FilterModal from './admin/FilterModal';
import SettingsModal from './admin/SettingsModal';
import ColumnSettingsModal from './admin/ColumnSettingsModal';

const AdminApp = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isFetchingNext, setIsFetchingNext] = useState(false);
    const [isFetchingPrev, setIsFetchingPrev] = useState(false);
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [filtersFields, setFiltersFields] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isColumnSettingsModalOpen, setIsColumnSettingsModalOpen] = useState(false);
    const [filterCount, setFilterCount] = useState(0);
    const rowsPerPage = 10; 

    const fetchData = (page) => {
       
        setIsLoading(true);

        const savedFilters = Cookies.get('product_filters');
        let filters = null;
        if (savedFilters) {
            try {
                filters = JSON.parse(savedFilters);
            } catch (e) {
                console.error('Could not parse product_filters cookie', e);
            }
        }
        const savedSettings = Cookies.get('admin_settings');
        const settings = savedSettings ? JSON.parse(savedSettings) : null;
        const savedGeneralSettings = Cookies.get('admin_general_settings');
        const generalSettings = savedGeneralSettings ? JSON.parse(savedGeneralSettings) : null;

        // Construct request data
        const requestData = { 
            security: wp_ajax.nonce,
            page: page,
            per_page: rowsPerPage,
        };

        // Only add filters if they exist
        if (filters && Object.keys(filters).length > 0) {
            requestData.filters = filters;
            setFilterCount(Object.keys(filters).length);
        } else {
            setFilterCount(0);
        }
        if (settings) {
            requestData.settings = settings;
        }
        if (generalSettings) {
            requestData.generalSettings = generalSettings;
        }
        console.log("settings",settings);

        return fetch(wp_ajax.getdata, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': wp_ajax.nonce,
            },
            body: JSON.stringify(requestData)
        })
        .then(response => response.json())
        .then(responseData => {
            console.log('Fetched Data:', responseData);
            setData(responseData.data);
            setTotalPages(responseData.total_pages);
            setColumns(responseData.header);
            setFiltersFields(responseData.filter);
            setIsFetchingNext(false);
            setIsFetchingPrev(false);
            setIsLoading(false);
        })
        .catch(error => {
            console.error('Error fetching data:', error);
        });
    };

    useEffect(() => {
        fetchData(currentPage);
    }, [currentPage]);

    

    const [modifiedCells, setModifiedCells] = useState([]);

    const updateMyData = (rowIndex, columnId, value) => {
        setData(oldData =>
            oldData.map((row, index) => {
                if (index === rowIndex) {
                    setModifiedCells(prev => [
                        ...prev.filter(cell => !(cell.rowIndex === rowIndex && cell.columnId === columnId)),
                        { 
                            rowIndex, 
                            id: row.id?.value ?? row.id, // Ensure `id` is a number
                            columnId, 
                            value 
                        },
                    ]);
                    return {
                        ...row,
                        [columnId]: { ...row[columnId], value }, // Preserve structure
                    };
                }
                return row;
            })
        );
    };



    const handleSave = () => {
        if (modifiedCells.length === 0) {
            alert('No changes to save.');
            return;
        }
        setIsSaving(true);

        const requestData = { 
            security: wp_ajax.nonce,
            modifiedCells
        };

        fetch(wp_ajax.savedata, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': wp_ajax.nonce,
            },
            body: JSON.stringify(requestData)
        })
        .then(response => response.json())
        .then(() => {
            setIsSaving(false);
            alert('Data saved successfully!');
            setModifiedCells([]); // Reset changes after saving
        })
        .catch(error => {
            setIsSaving(false);
            console.error('Error saving data:', error);
        });
    };


    const goToNextPage = () => {
        setIsFetchingNext(true);
        setCurrentPage(prev => Math.min(prev + 1, totalPages));
   
    };

    const goToPreviousPage = () => {
        setIsFetchingPrev(true);
        setCurrentPage(prev => Math.max(prev - 1, 1));
        
    };

     const applyFilter = (filters) => {
        console.log("filters",filters);
        setIsFilterModalOpen(false);
        fetchData(1);
    };


    return (
        <Panel>
            <PanelBody>
            <div className="filterbar">
                <div>
                    <Button isPrimary onClick={() => setIsFilterModalOpen(true)}>
                        Filter {filterCount > 0 && <span className="btncounter">{filterCount}</span>}
                    </Button>
                </div>
                <div>
                    <Button isPrimary onClick={() => setIsColumnSettingsModalOpen(true)}>
                        Column
                    </Button>
                </div>
                <div>
                    <Button isPrimary onClick={() => setIsSettingsModalOpen(true)}>
                        Settings
                    </Button>
                </div>
               
                <div>
                   <Button 
                        isPrimary
                        disabled={modifiedCells.length === 0 || isSaving} 
                        onClick={handleSave}
                    >
                        {isSaving ? <Spinner /> : (
                            <>
                                Save Settings 
                                {modifiedCells.length > 0 && <span className="btncounter">{modifiedCells.length}</span>}
                            </>
                        )}
                    </Button>

                </div>
                 <div>
                    <Button isPrimary onClick={() => window.open('https://www.codesmade.com/contact-us/', '_blank')} className="supportbutton">
                        24/7 Support
                    </Button>
                </div>
                <div className="pagination-controls">
                    <Button isPrimary onClick={goToPreviousPage} disabled={currentPage === 1 || isFetchingPrev}>
                        {isFetchingPrev ? <Spinner /> : 'Previous'}
                    </Button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <Button isPrimary onClick={goToNextPage} disabled={currentPage === totalPages || isFetchingNext}>
                        {isFetchingNext ? <Spinner /> : 'Next'}
                    </Button>
                </div>

            </div>
            {/* Show content loader spinner */}
            {isLoading ? <Spinner /> : <Table 
    columns={columns} 
    data={data} 
    updateMyData={updateMyData} 
    onRefresh={() => fetchData(currentPage)}
    modifiedCells={modifiedCells}
/>}
            

            
            
            </PanelBody>
            <FilterModal  filtersFields={filtersFields} isOpen={isFilterModalOpen} onClose={() => setIsFilterModalOpen(false)} onApplyFilter={applyFilter} />
            
            <ColumnSettingsModal
                columns={columns}
                isOpen={isColumnSettingsModalOpen}
                onClose={() => {
                    setIsColumnSettingsModalOpen(false);
                }}
                onApplySetting={() => {
                    fetchData(1); // Refresh data when column settings modal is closed
                }}
            />
            <SettingsModal
                    isOpen={isSettingsModalOpen}
                    onClose={() => setIsSettingsModalOpen(false)}
                    onApplySetting={(settings) => {
                        fetchData(1); // Refresh data when column settings modal is closed
                    }}
                    sortOptions={[
                        { label: 'Product Name', value: 'name' },
                        { label: 'Price', value: 'price' },
                        { label: 'Stock', value: 'stock' },
                    ]}
           />


        </Panel>
    );
};

domReady(() => {
    const root = createRoot(document.getElementById('gmbpu-admin-root'));
    root.render(<AdminApp />);
});

export default AdminApp;
